/**
 * 订阅中心（重写）。
 *
 * 移除的旧功能（均为假数据/无效配置）：
 * - 推送时间规则（开市前分钟数 / 非交易日时间点）—— 推送节奏由 SCF Timer 控制，页面无法改
 * - 百度翻译 API 配置 —— 翻译已由后端 DeepSeek 统一负责
 * - 消息源管理（mock SOURCES）—— 消息源在后端 collectors 固化
 * - GitHub Actions 部署指南 —— 部署改为 SCF
 *
 * 新功能：
 * - 全局 pushplus token 配置（写入每条订阅的 pushplusToken 或提示在控制面板触发）
 * - 订阅 CRUD：编辑（max_per_push / 维度 / 影响度 / token）、启用开关、删除
 * - 真实推送历史（来自 /api/push/history）
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Send, CheckCircle2, AlertCircle,
  Pencil, Trash2, RefreshCw, Loader2, ExternalLink, Plus,
} from "lucide-react";
import { useApiStore } from "@/store/useApiStore";
import SubEditModal from "@/components/SubEditModal";
import { StockApi, type Subscription } from "@/services/api";
import { cn } from "@/lib/utils";
import { isTradingDay } from "@/lib/trading";
import { fmtDate } from "@/lib/format";

export default function SubscriptionPage() {
  const nav = useNavigate();
  const {
    subscriptions, loading, error, refresh,
    updateSub, removeSub,
  } = useApiStore();

  const [editing, setEditing] = useState<Subscription | null>(null);
  const [togglingCode, setTogglingCode] = useState<string | null>(null);
  // 两步删除确认（替代原生 confirm：移动端浏览器对 confirm 支持差，可能完全无反应）
  const [confirmingCode, setConfirmingCode] = useState<string | null>(null);

  // 添加订阅：搜索框
  const [addKw, setAddKw] = useState("");
  const [addResults, setAddResults] = useState<{ code: string; name: string; mkt: string; market: string }[]>([]);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 搜索防抖
  useEffect(() => {
    const t = setTimeout(async () => {
      const kw = addKw.trim();
      if (!kw) { setAddResults([]); return; }
      try {
        const r = await StockApi.search(kw, 8);
        setAddResults(r.map((s) => ({
          code: s.stockCode, name: s.stockName, mkt: s.mktNum, market: s.market,
        })));
      } catch {
        setAddResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [addKw]);

  const addSub = async (code: string, name: string, mkt: string) => {
    setAdding(true);
    setAddError("");
    try {
      await useApiStore.getState().createSub(code, name, mkt);
      setAddKw("");
      setAddResults([]);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : "添加失败");
    } finally {
      setAdding(false);
    }
  };

  const toggleSub = async (code: string, current: boolean) => {
    setTogglingCode(code);
    try {
      await updateSub(code, { enabled: !current });
    } finally {
      setTogglingCode(null);
    }
  };

  const del = async (code: string) => {
    // 第一次点击进入确认态，第二次点击才真正删除（3秒无操作自动恢复）
    if (confirmingCode !== code) {
      setConfirmingCode(code);
      setTimeout(() => setConfirmingCode((c) => (c === code ? null : c)), 3000);
      return;
    }
    setConfirmingCode(null);
    await removeSub(code);
  };

  const isTrading = isTradingDay();
  const enabledCount = subscriptions.filter((s) => s.enabled).length;

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-[1200px] mx-auto">
      <header className="animate-riseIn">
        <h1 className="font-serif text-2xl text-ink-100 mb-1 title-bar">订阅中心</h1>
        <p className="text-sm text-ink-400">
          管理推送通道与订阅股票 · 推送节奏由云函数 Timer 控制（{isTrading ? "交易日" : "非交易日"}）
        </p>
      </header>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-bear/40 bg-bear/10 text-bear text-sm">
          <AlertCircle size={14} />{error}
          <button onClick={() => useApiStore.setState({ error: "" })} className="ml-auto text-bear/60 hover:text-bear">×</button>
        </div>
      )}

      {/* 推送通道说明 */}
      <section className="card p-5 border-gold/30 animate-riseIn">
        <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2 mb-3">
          <Send size={16} className="text-gold" />
          推送通道 · pushplus
          <span className="label-tag text-gold border-gold/40 bg-gold/10">微信接收</span>
        </h2>
        <p className="text-sm text-ink-400 mb-3 leading-relaxed">
          推送 token 在后端 <code className="text-gold">.env</code> 的 <code className="text-gold">PUSHPLUS_TOKEN</code> 配置，
          或为单只股票在下方编辑里设置专属 token。获取 token：
          <a href="http://www.pushplus.plus/" target="_blank" rel="noreferrer" className="text-gold underline inline-flex items-center gap-0.5 ml-1">
            pushplus.plus <ExternalLink size={11} />
          </a>
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <ChannelStat label="合并推送" value="默认开启" highlight />
          <ChannelStat label="订阅股票" value={`${subscriptions.length} 只`} />
          <ChannelStat label="已启用" value={`${enabledCount} 只`} />
          <ChannelStat label="去重" value="标题相似度" />
        </div>
      </section>

      {/* 添加订阅 */}
      <section className="card p-5 animate-riseIn relative z-20">
        <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2 mb-3">
          <Plus size={16} className="text-gold" />
          添加订阅
        </h2>
        <div className="relative">
          <input
            value={addKw}
            onChange={(e) => setAddKw(e.target.value)}
            placeholder="输入股票代码或名称（如 600519、茅台、AAPL）"
            className="w-full bg-ink-900 border border-ink-700 focus:border-gold/60 px-4 py-2.5 text-sm text-ink-100 outline-none"
          />
          {addResults.length > 0 && (
            <div className="absolute z-50 mt-1 w-full border border-ink-600 bg-ink-850 shadow-2xl shadow-black/60 max-h-80 overflow-auto">
              {addResults.map((s) => (
                <button
                  key={`${s.mkt}-${s.code}`}
                  onClick={() => addSub(s.code, s.name, s.mkt)}
                  disabled={adding}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-ink-800 transition-colors border-b border-ink-800 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink-100 truncate">{s.name}</div>
                    <div className="num text-[0.6875rem] text-ink-400">
                      {s.code}<span className="ml-2 text-gold">{s.market}</span>
                    </div>
                  </div>
                  <Plus size={14} className="text-gold shrink-0" />
                </button>
              ))}
            </div>
          )}
          {addError && <div className="text-xs text-bear mt-2">{addError}</div>}
        </div>
      </section>

      {/* 订阅列表 */}
      <section className="card p-5 animate-riseIn">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2">
            <Bell size={16} className="text-gold" />
            我的订阅
            <span className="num text-xs text-ink-500">({subscriptions.length})</span>
          </h2>
          <button onClick={() => refresh(true)} className="text-xs text-ink-400 hover:text-gold flex items-center gap-1">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> 刷新
          </button>
        </div>

        {loading && subscriptions.length === 0 ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="skeleton h-16 w-full rounded-md" />
            ))}
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-10">
            <Bell size={28} className="text-ink-600 mx-auto mb-3" />
            <div className="text-sm text-ink-300 mb-1">尚未订阅任何股票</div>
            <p className="text-xs text-ink-500">在上方搜索框添加你的第一只订阅</p>
          </div>
        ) : (
          <div className="space-y-2">
            {subscriptions.map((s) => (
              <div
                key={s.stockCode}
                className={cn(
                  "flex items-center justify-between p-3 border transition-colors",
                  s.enabled ? "border-ink-600 bg-ink-900/40" : "border-ink-800 opacity-60",
                )}
              >
                <button
                  onClick={() => nav(`/stock/${s.stockCode}?mkt=${s.mktNum}`)}
                  className="text-left min-w-0"
                >
                  <div className="text-sm text-ink-100 hover:text-gold transition-colors truncate">
                    {s.stockName}
                    <span className="num text-[0.6875rem] text-ink-500 ml-2">{s.stockCode}</span>
                  </div>
                  <div className="num text-[0.6875rem] text-ink-500 mt-0.5">
                    上限 {s.maxPerPush} 条 · {s.minImpact === "high" ? "高影响" : s.minImpact === "medium" ? "中影响+" : "全部"}
                    {s.lastPushedAt && ` · 上次 ${fmtDate(s.lastPushedAt)}`}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {/* 启用开关 */}
                  <button
                    onClick={() => toggleSub(s.stockCode, s.enabled)}
                    disabled={togglingCode === s.stockCode}
                    title={s.enabled ? "已启用" : "已暂停"}
                    className={cn(
                      "w-11 h-6 rounded-full transition-colors flex items-center px-0.5",
                      s.enabled ? "bg-gold justify-end" : "bg-ink-700 justify-start",
                      togglingCode === s.stockCode && "opacity-50",
                    )}
                  >
                    {togglingCode === s.stockCode
                      ? <Loader2 size={16} className="animate-spin text-ink-300" />
                      : <span className="w-5 h-5 rounded-full bg-ink-950 transition-all" />}
                  </button>
                  {/* 编辑 */}
                  <button
                    onClick={() => setEditing(s)}
                    title="编辑"
                    aria-label={`编辑 ${s.stockName}`}
                    className="text-ink-500 hover:text-gold p-2"
                  >
                    <Pencil size={16} />
                  </button>
                  {/* 删除（两步确认：第一次点变红色确认态） */}
                  <button
                    onClick={() => del(s.stockCode)}
                    title="删除"
                    aria-label={`删除 ${s.stockName}`}
                    className={cn(
                      "p-2 transition-colors",
                      confirmingCode === s.stockCode
                        ? "text-bear bg-bear/10 rounded"
                        : "text-ink-500 hover:text-bear",
                    )}
                  >
                    {confirmingCode === s.stockCode
                      ? <span className="text-xs font-medium">确认?</span>
                      : <Trash2 size={16} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {editing && (
        <SubEditModal
          sub={editing}
          onClose={() => setEditing(null)}
          onSave={(patch) => updateSub(editing.stockCode, patch).then(() => undefined)}
        />
      )}
    </div>
  );
}

function ChannelStat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="border border-ink-700 p-3">
      <div className="text-[0.6875rem] text-ink-500 mb-1">{label}</div>
      <div className={cn("text-sm font-medium num", highlight ? "text-gold" : "text-ink-200")}>{value}</div>
    </div>
  );
}
